import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, output } from '@angular/core';
import { FilterAttributeOptionComponentDef } from '@df/catalog-ui/listing/filter/filter-attribute-option.component.def';
import { Model } from '@df/catalog/model';
import { DfEventService } from '@df/core/service/df-event';
import { FacetUrlPipe } from '@df/module-facet-url/util/facet-url.pipe';
import { DfImageDirective } from '@df/module-image/df-image.directive';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { HideUpComponent } from '@df/ui/hide-up/hide-up.component';
import { ToggleModule } from '@df/ui/toggle/toggle.module';
import { ToggleService } from '@df/ui/toggle/toggle.service';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { Subscription } from 'rxjs';
import { DIALOG_EVENT, DialogEventType } from '../../../../../common/dialog/dialog.interface';
import { FilterStateService } from '../filter-state.service';

@Component({
    selector: 'filter-attribute-option',
    templateUrl: './filter-attribute-option.html',
    styleUrl: './filter-attribute-option.scss',
    standalone: true,
    imports: [CommonModule, DfImageDirective, FacetUrlPipe, GridModule, HideUpComponent, HrefComponent, ToggleModule, TranslatePipe],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FilterAttributeOptionComponent<T extends Model> extends FilterAttributeOptionComponentDef<T> {
    readonly onFilterOpen = output<string>();

    protected isDefaultOpen = false;
    private dialogEventSubscription?: any;
    private toggleStateSubscription?: Subscription;
    private stateChangeSubscription?: Subscription;

    private dfEventService = inject(DfEventService);
    private toggleService = inject(ToggleService);
    private filterStateService = inject(FilterStateService);

    override ngOnInit() {
        super.ngOnInit();

        // Set the current category ID in the service to handle category changes
        const categoryId = this.getCategoryId();
        this.filterStateService.setCategoryId(categoryId);

        // Register this component with the service
        this.filterStateService.registerFilterComponent(this.filter.attribute.id, this.filter.isDefaultOpen);

        // Get the current default open state from the service (persists across component lifecycle)
        this.isDefaultOpen = this.filterStateService.getFilterDefaultOpenState(this.filter.attribute.id);

        this.detectChanges();

        // Subscribe to state changes from the service
        this.stateChangeSubscription = this.filterStateService.getStateChanges().subscribe(() => {
            // Update this component's state when global state changes
            this.isDefaultOpen = this.filterStateService.getFilterDefaultOpenState(this.filter.attribute.id);
            this.detectChanges();
        });

        // Listen for dialog open events to detect when filters modal is opened
        this.dialogEventSubscription = this.dfEventService.success((event: any) => {
            if (event.id === DIALOG_EVENT && event.data && event.data.type === DialogEventType.Open && event.data.id === 'filters-dialog') {
                // Filters modal opened - notify service and start monitoring
                if (!this.filterStateService.hasFiltersModalBeenOpened()) {
                    this.filterStateService.setFiltersModalOpened();
                    this.startMonitoringFilterInteractions();
                }
            }
        });
    }

    /**
     * Start monitoring individual filter toggle interactions after modal is opened
     */
    private startMonitoringFilterInteractions() {
        // Subscribe to the 'filters' toggle group state changes
        this.toggleStateSubscription = this.toggleService.getState('filters').subscribe(groupState => {
            if (groupState && !this.filterStateService.hasUserInteractedWithFilters()) {
                // Get all currently open filter IDs
                const filtersGroup = this.toggleService.getGroup('filters');
                const openItems = filtersGroup.getItems().filter(item => item.isOpen);
                const openFilterIds = openItems.map(item => String(item.id[1]));

                // Check if any non-default filter was opened
                const hasNonDefaultFilterOpened = this.filterStateService.checkForNonDefaultFilterInteraction(openFilterIds);

                if (hasNonDefaultFilterOpened) {
                    // Notify service that user has interacted with filters
                    this.filterStateService.setUserHasInteractedWithFilters();
                }
            }
        });
    }

    /**
     * Get the current category ID from the listing source
     */
    private getCategoryId(): string | number | null {
        // The listing config.source contains the category model for category pages
        const source = this.filter.listing.config.source;

        if (source && source.id) {
            return source.id;
        }

        // Fallback: try to get category ID from URL or other sources
        // This could be enhanced to use ActivatedRoute if needed
        return null;
    }

    override ngOnDestroy() {
        super.ngOnDestroy();
        this.dialogEventSubscription?.unsubscribe();
        this.toggleStateSubscription?.unsubscribe();
        this.stateChangeSubscription?.unsubscribe();
    }
}
